import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Select,
  DatePicker,
  Button,
  Table,
  Tag,
  Space,
  Statistic,
  Tabs,
  Slider,
  theme,
  Spin,
} from "antd";
import {
  DownloadOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  StepForwardOutlined,
  StepBackwardOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ReferenceLine,
} from "recharts";
import PageLayout from "@components/pageLayout/PageLayout.react";
import { makePagination } from "@components/common/TablePagination";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

export default function DispatchReport() {
  const { token } = theme.useToken();
  const [selectedDispatch, setSelectedDispatch] = useState(null);
  const [dateRange, setDateRange] = useState([dayjs().subtract(7, "day"), dayjs()]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackPosition, setPlaybackPosition] = useState(0);
  const [activeTab, setActiveTab] = useState("report");
  const [historicalDispatches, setHistoricalDispatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState(10);

  // Fetch historical dispatches from API
  const fetchHistoricalDispatches = useCallback(async () => {
    setLoading(true);
    try {
      const authToken = localStorage.getItem("token");
      // Fetch completed and cancelled dispatches (past dispatches)
      const response = await fetch("/api/dispatch/list?status=completed,cancelled", {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await response.json();
      if (data.success) {
        const dispatches = data.data?.dispatches || [];
        // Transform to table format
        const transformed = dispatches.map((d) => ({
          id: d.id,
          type: d.dispatchType || "MANUAL",
          time: d.actualStart || d.scheduledStart
            ? dayjs(d.actualStart || d.scheduledStart).format("YYYY-MM-DD HH:mm")
            : "-",
          systems: d.totalDevices || 0,
          energy: d.actualEnergyKwh || 0,
          revenue: d.revenue || 0,
          score: d.performanceScore || 0,
          status: d.status,
          // Keep raw data for report view
          raw: d,
        }));
        setHistoricalDispatches(transformed);
        // Select first dispatch by default
        if (transformed.length > 0 && !selectedDispatch) {
          setSelectedDispatch(transformed[0].id);
        }
      }
    } catch (error) {
      console.error("Failed to fetch historical dispatches:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedDispatch]);

  useEffect(() => {
    fetchHistoricalDispatches();
  }, []);

  // Generate dispatch options from fetched data
  const dispatchOptions = historicalDispatches.map((d) => ({
    value: d.id,
    label: `${d.id} - ${d.type} (${d.time})`,
  }));

  // Get selected dispatch data for report
  const selectedDispatchData = historicalDispatches.find((d) => d.id === selectedDispatch);
  const rawData = selectedDispatchData?.raw || {};

  // Report data from selected dispatch
  const reportData = {
    id: rawData.id || "-",
    type: rawData.dispatchType || "MANUAL",
    startTime: rawData.actualStart || rawData.scheduledStart || "-",
    endTime: rawData.actualEnd || rawData.scheduledEnd || "-",
    duration: rawData.durationSeconds || 0,
    targetPower: rawData.targetPowerKw || 0,
    avgPower: rawData.actualPowerKw || 0,
    maxPower: rawData.maxPowerKw || 0,
    minPower: rawData.minPowerKw || 0,
    totalEnergy: rawData.actualEnergyKwh || 0,
    revenue: rawData.revenue || 0,
    totalSystems: rawData.totalDevices || 0,
    respondingSystems: rawData.participatingDevices || 0,
    responseRate: rawData.totalDevices
      ? ((rawData.participatingDevices || 0) / rawData.totalDevices * 100)
      : 0,
    avgResponseTime: rawData.avgResponseTimeMs ? rawData.avgResponseTimeMs / 1000 : 0,
    performanceScore: rawData.performanceScore || 0,
  };

  // Playback data - no telemetry API available yet, show empty state
  const playbackData = [];

  const columns = [
    { title: "Dispatch ID", dataIndex: "id", key: "id" },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (type) => {
        const colors = {
          "FCAS_RAISE": "blue",
          "FCAS_LOWER": "green",
          "PEAK_SHAVING": "orange",
          "ARBITRAGE": "purple",
          "MANUAL": "default",
        };
        return <Tag color={colors[type] || "default"}>{type || "MANUAL"}</Tag>;
      },
    },
    { title: "Time", dataIndex: "time", key: "time" },
    { title: "Systems", dataIndex: "systems", key: "systems", align: "center" },
    {
      title: "Energy (kWh)",
      dataIndex: "energy",
      key: "energy",
      align: "right",
      render: (v) => (v ? Number(v).toFixed(2) : "-"),
    },
    {
      title: "Revenue ($)",
      dataIndex: "revenue",
      key: "revenue",
      align: "right",
      render: (v) => (v ? `$${Number(v).toFixed(2)}` : "-"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      render: (status) => {
        const colors = {
          completed: "success",
          cancelled: "error",
          error: "error",
        };
        return <Tag color={colors[status] || "default"}>{status}</Tag>;
      },
    },
    {
      title: "Score",
      dataIndex: "score",
      key: "score",
      render: (v) =>
        v ? (
          <Tag color={v >= 98 ? "success" : v >= 95 ? "processing" : "warning"}>
            {Number(v).toFixed(1)}%
          </Tag>
        ) : (
          "-"
        ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" onClick={() => setSelectedDispatch(record.id)}>
            View Report
          </Button>
          <Button type="link" size="small" onClick={() => { setSelectedDispatch(record.id); setActiveTab("playback"); }}>
            Playback
          </Button>
        </Space>
      ),
    },
  ];

  const tabItems = [
    {
      key: "report",
      label: "Report",
      children: (
        <div>
          {/* Report Stats */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={12} sm={8} md={4}>
              <Statistic title="Target Power" value={reportData.targetPower} suffix="kW" />
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Statistic title="Avg Power" value={reportData.avgPower} suffix="kW" valueStyle={{ color: token.colorSuccess }} />
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Statistic title="Total Energy" value={reportData.totalEnergy} suffix="kWh" precision={2} />
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Statistic title="Revenue" value={reportData.revenue} prefix="$" precision={2} valueStyle={{ color: token.colorSuccess }} />
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Statistic title="Response Rate" value={reportData.responseRate} suffix="%" precision={1} />
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Statistic
                title="Performance Score"
                value={reportData.performanceScore}
                suffix="%"
                precision={1}
                valueStyle={{ color: reportData.performanceScore >= 98 ? token.colorSuccess : token.colorWarning }}
              />
            </Col>
          </Row>

          {/* Power Chart */}
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <div style={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={playbackData} margin={{ top: 10, right: 10, bottom: 5, left: 0 }}>
                  <defs>
                    <linearGradient id="actualGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={token.colorSuccess} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={token.colorSuccess} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} />
                  <XAxis dataKey="time" tick={{ fontSize: 10 }} interval={9} />
                  <YAxis tick={{ fontSize: 11 }} domain={[2500, 3500]} />
                  <RechartsTooltip />
                  <Legend />
                  <ReferenceLine y={reportData.targetPower} stroke={token.colorPrimary} strokeDasharray="5 5" label="Target" />
                  <Area type="monotone" dataKey="actual" name="Actual Power" stroke={token.colorSuccess} fill="url(#actualGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>
      ),
    },
    {
      key: "playback",
      label: "Playback",
      children: (
        <div>
          {playbackData.length > 0 ? (
            <>
              {/* Playback Controls */}
              <Card bordered={false} style={{ borderRadius: 12, marginBottom: 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                  <Button icon={<StepBackwardOutlined />} onClick={() => setPlaybackPosition(0)} />
                  <Button
                    type="primary"
                    icon={isPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                    onClick={() => setIsPlaying(!isPlaying)}
                  >
                    {isPlaying ? "Pause" : "Play"}
                  </Button>
                  <Button icon={<StepForwardOutlined />} onClick={() => setPlaybackPosition(playbackData.length - 1)} />
                  <div style={{ flex: 1, padding: "0 16px" }}>
                    <Slider
                      min={0}
                      max={Math.max(playbackData.length - 1, 0)}
                      value={playbackPosition}
                      onChange={setPlaybackPosition}
                      tooltip={{ formatter: (v) => playbackData[v]?.time }}
                    />
                  </div>
                  <Text strong style={{ minWidth: 80, textAlign: "right" }}>
                    {playbackData[playbackPosition]?.time || "-"}
                  </Text>
                </div>
              </Card>

              {/* Playback Stats */}
              <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col xs={8}>
                  <Card bordered={false} style={{ borderRadius: 12, textAlign: "center" }}>
                    <Text type="secondary">Time</Text>
                    <Title level={3} style={{ margin: 0 }}>{playbackData[playbackPosition]?.time || "-"}</Title>
                  </Card>
                </Col>
                <Col xs={8}>
                  <Card bordered={false} style={{ borderRadius: 12, textAlign: "center" }}>
                    <Text type="secondary">Power Output</Text>
                    <Title level={3} style={{ margin: 0, color: token.colorSuccess }}>
                      {playbackData[playbackPosition]?.actual?.toLocaleString() || "0"} kW
                    </Title>
                  </Card>
                </Col>
                <Col xs={8}>
                  <Card bordered={false} style={{ borderRadius: 12, textAlign: "center" }}>
                    <Text type="secondary">Responding Systems</Text>
                    <Title level={3} style={{ margin: 0, color: token.colorPrimary }}>
                      {playbackData[playbackPosition]?.systems || 0}
                    </Title>
                  </Card>
                </Col>
              </Row>

              {/* Playback Chart */}
              <Card bordered={false} style={{ borderRadius: 12 }}>
                <div style={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={playbackData.slice(0, playbackPosition + 1)} margin={{ top: 10, right: 10, bottom: 5, left: 0 }}>
                      <defs>
                        <linearGradient id="playbackGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={token.colorSuccess} stopOpacity={0.3} />
                          <stop offset="95%" stopColor={token.colorSuccess} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} />
                      <XAxis dataKey="time" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 11 }} domain={["auto", "auto"]} />
                      <RechartsTooltip />
                      <ReferenceLine y={reportData.targetPower} stroke={token.colorPrimary} strokeDasharray="5 5" />
                      <Area type="monotone" dataKey="actual" name="Actual Power" stroke={token.colorSuccess} fill="url(#playbackGrad)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </>
          ) : (
            <Card bordered={false} style={{ borderRadius: 12, textAlign: "center", padding: 48 }}>
              <PlayCircleOutlined style={{ fontSize: 48, color: token.colorTextSecondary, marginBottom: 16 }} />
              <Title level={4} type="secondary">Playback Not Available</Title>
              <Text type="secondary">
                Telemetry playback data is not yet available for this dispatch. This feature will be enabled when real-time telemetry recording is active.
              </Text>
            </Card>
          )}
        </div>
      ),
    },
  ];

  return (
    <PageLayout active="dispatchReport">
      {/* Page Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <Title level={2} style={{ marginBottom: 4 }}>
              Dispatch Report
            </Title>
            <Text type="secondary" style={{ fontSize: 16 }}>
              Historical dispatch reports and playback
            </Text>
          </div>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchHistoricalDispatches} loading={loading}>
              Refresh
            </Button>
            <Button type="primary" icon={<DownloadOutlined />} size="large">
              Export Report
            </Button>
          </Space>
        </div>
      </div>

      {/* Filters */}
      <Card bordered={false} style={{ marginBottom: 24, borderRadius: 12 }}>
        <Space wrap size="middle">
          <div>
            <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
              Select Dispatch
            </Text>
            <Select
              value={selectedDispatch}
              onChange={setSelectedDispatch}
              options={dispatchOptions}
              style={{ width: 350 }}
              size="large"
              loading={loading}
              placeholder="Select a dispatch"
              notFoundContent={loading ? <Spin size="small" /> : "No dispatches found"}
            />
          </div>
          <div>
            <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
              Date Range
            </Text>
            <RangePicker
              value={dateRange}
              onChange={setDateRange}
              size="large"
              style={{ width: 280 }}
            />
          </div>
        </Space>
      </Card>

      {/* Report/Playback Tabs */}
      <Card bordered={false} style={{ borderRadius: 12, marginBottom: 24 }}>
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
      </Card>

      {/* Historical Dispatches Table */}
      <Card
        bordered={false}
        style={{ borderRadius: 12 }}
        title={<Text strong>Historical Dispatches</Text>}
      >
        <Table
          columns={columns}
          dataSource={historicalDispatches}
          rowKey="id"
          loading={loading}
          pagination={makePagination(pageSize, setPageSize)}
        />
      </Card>
    </PageLayout>
  );
}
